import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertCircle, CreditCard, Lock } from 'lucide-react';
export function CheckoutPage() {
  const [status, setStatus] = useState<'form' | 'success' | 'failure'>('form');
  if (status === 'success') {
    return <div className="min-h-screen bg-background flex items-center justify-center p-6" data-id="element-5491">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-surface border border-border rounded-xl p-8 max-w-md w-full text-center shadow-xl" data-id="element-5492">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6" data-id="element-5493">
            <CheckCircle2 className="w-8 h-8 text-green-500" data-id="element-5494" />
          </div>
          <h1 className="text-2xl font-bold mb-2" data-id="element-5495">Welcome to Booker!</h1>
          <p className="text-textMuted text-sm mb-8" data-id="element-5496">
            Your subscription is active and you're ready to start booking.
          </p>
          <Link to="/app" className="block w-full py-3 bg-primary text-background rounded-lg font-medium hover:bg-primaryHover transition-colors shadow-[0_0_15px_rgba(245,158,11,0.2)]" data-id="element-5497">
            Go to dashboard
          </Link>
        </motion.div>
      </div>;
  }
  if (status === 'failure') {
    return <div className="min-h-screen bg-background flex items-center justify-center p-6" data-id="element-5498">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-surface border border-border rounded-xl p-8 max-w-md w-full text-center shadow-xl" data-id="element-5499">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6" data-id="element-5500">
            <AlertCircle className="w-8 h-8 text-red-500" data-id="element-5501" />
          </div>
          <h1 className="text-2xl font-bold mb-2" data-id="element-5502">Payment failed</h1>
          <p className="text-textMuted text-sm mb-8" data-id="element-5503">
            We couldn't process your payment. Please check your card details and
            try again.
          </p>
          <button onClick={() => setStatus('form')} className="w-full py-3 bg-background border border-border rounded-lg font-medium hover:bg-surfaceHover transition-colors" data-id="element-5504">
            Try again
          </button>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-background py-12 px-6" data-id="element-5505">
      <div className="max-w-5xl mx-auto" data-id="element-5506">
        <Link to="/" className="flex items-center gap-2 mb-10" data-id="element-5507">
          <span className="text-2xl font-bold tracking-tight text-textMain" data-id="element-5508">
            Booker
          </span>
          <div className="w-2 h-2 rounded-full bg-primary mt-1" data-id="element-5509"></div>
        </Link>

        <div className="flex flex-col lg:flex-row gap-10" data-id="element-5510">
          {/* Left Col - Form */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="flex-1" data-id="element-5511">
            <h1 className="text-2xl font-bold mb-6" data-id="element-5512">Checkout</h1>

            <form className="space-y-8" onSubmit={e => {
            e.preventDefault();
            setStatus('success');
          }} data-id="element-5513">
              {/* Billing Info */}
              <div className="space-y-4" data-id="element-5514">
                <h2 className="text-lg font-semibold border-b border-border pb-2" data-id="element-5515">
                  Billing Information
                </h2>
                <div className="grid grid-cols-2 gap-4" data-id="element-5516">
                  <div className="col-span-2" data-id="element-5517">
                    <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5518">
                      Name on card
                    </label>
                    <input type="text" className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors" placeholder="Jane Doe" required data-id="element-5519" />
                  </div>
                  <div className="col-span-2" data-id="element-5520">
                    <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5521">
                      Billing address
                    </label>
                    <input type="text" className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors" placeholder="123 Main St" required data-id="element-5522" />
                  </div>
                  <div data-id="element-5523">
                    <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5524">
                      City
                    </label>
                    <input type="text" className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors" placeholder="New York" required data-id="element-5525" />
                  </div>
                  <div data-id="element-5526">
                    <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5527">
                      ZIP / Postal Code
                    </label>
                    <input type="text" className="w-full px-4 py-2.5 bg-surface border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors" placeholder="10001" required data-id="element-5528" />
                  </div>
                </div>
              </div>

              {/* Payment Info */}
              <div className="space-y-4" data-id="element-5529">
                <h2 className="text-lg font-semibold border-b border-border pb-2" data-id="element-5530">
                  Payment Method
                </h2>
                <div className="bg-surface border border-border rounded-xl p-4" data-id="element-5531">
                  <div className="mb-4" data-id="element-5532">
                    <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5533">
                      Card number
                    </label>
                    <div className="relative" data-id="element-5534">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-textMuted" data-id="element-5535" />
                      <input type="text" className="w-full pl-10 pr-4 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors font-mono" placeholder="0000 0000 0000 0000" required data-id="element-5536" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-5537">
                    <div data-id="element-5538">
                      <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5539">
                        Expiry date
                      </label>
                      <input type="text" className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors font-mono" placeholder="MM/YY" required data-id="element-5540" />
                    </div>
                    <div data-id="element-5541">
                      <label className="block text-sm font-medium text-textMuted mb-1.5" data-id="element-5542">
                        CVC
                      </label>
                      <input type="text" className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-sm focus:outline-none focus:border-primary transition-colors font-mono" placeholder="123" required data-id="element-5543" />
                    </div>
                  </div>
                </div>
              </div>

              <button className="w-full py-3 bg-primary text-background rounded-lg font-medium hover:bg-primaryHover transition-colors shadow-[0_0_15px_rgba(245,158,11,0.2)] flex items-center justify-center gap-2" data-id="element-5544">
                <Lock className="w-4 h-4" data-id="element-5545" /> Complete purchase
              </button>
            </form>
          </motion.div>

          {/* Right Col - Summary */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="w-full lg:w-96 shrink-0" data-id="element-5546">
            <div className="bg-surface border border-border rounded-xl p-6 sticky top-6" data-id="element-5547">
              <h2 className="text-lg font-semibold mb-6" data-id="element-5548">Order Summary</h2>

              <div className="flex justify-between items-start mb-4" data-id="element-5549">
                <div data-id="element-5550">
                  <h3 className="font-medium" data-id="element-5551">Booker Pro</h3>
                  <p className="text-sm text-textMuted" data-id="element-5552">Billed annually</p>
                </div>
                <span className="font-medium" data-id="element-5553">$468.00</span>
              </div>

              <div className="h-px bg-border my-4" data-id="element-5554"></div>

              <div className="space-y-3 text-sm mb-4" data-id="element-5555">
                <div className="flex justify-between text-textMuted" data-id="element-5556">
                  <span data-id="element-5557">Subtotal</span>
                  <span data-id="element-5558">$468.00</span>
                </div>
                <div className="flex justify-between text-textMuted" data-id="element-5559">
                  <span data-id="element-5560">Tax</span>
                  <span data-id="element-5561">Calculated at next step</span>
                </div>
              </div>

              <div className="h-px bg-border my-4" data-id="element-5562"></div>

              <div className="flex justify-between items-center mb-6" data-id="element-5563">
                <span className="font-semibold" data-id="element-5564">Total due today</span>
                <span className="text-2xl font-bold" data-id="element-5565">$468.00</span>
              </div>

              <div className="bg-background border border-border rounded-lg p-3 flex items-start gap-3" data-id="element-5566">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" data-id="element-5567" />
                <p className="text-xs text-textMuted leading-relaxed" data-id="element-5568">
                  Includes a 14-day free trial. You won't be charged until your
                  trial ends on April 20, 2026. Cancel anytime.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}